import { useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

type WebSocketEvent = {
  event: string;
  data: any;
};

type WebSocketEventHandler = (data: any) => void;

interface UseWebSocketOptions {
  onUploadCompleted?: WebSocketEventHandler;
  onUploadReviewed?: WebSocketEventHandler;
  onAuthSuccess?: WebSocketEventHandler;
  onContentGenerated?: WebSocketEventHandler;
  onContentGenerationFailed?: WebSocketEventHandler;
}

export function useWebSocket(options: UseWebSocketOptions) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectedRef = useRef(false);

  const { onUploadCompleted, onUploadReviewed, onAuthSuccess, onContentGenerated, onContentGenerationFailed } = options;

  const connectWebSocket = useCallback(() => {
    if (isConnectedRef.current) return;

    try {
      // Get WebSocket URL - use same host as current page
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;
      
      console.log('🔗 Connecting to WebSocket:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('📡 WebSocket connected');
        isConnectedRef.current = true;
        
        // No need to send auth - server validates session automatically on connection
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketEvent = JSON.parse(event.data);
          console.log('📨 WebSocket message:', message);

          switch (message.event) {
            case 'auth_success':
              console.log('✅ WebSocket authenticated for user:', message.data.userId);
              onAuthSuccess?.(message.data);
              break;
            case 'upload_completed':
              console.log('📸 Upload completed:', message.data);
              onUploadCompleted?.(message.data);
              break;
            case 'upload_reviewed':
              console.log('✅ Upload reviewed:', message.data);
              onUploadReviewed?.(message.data);
              break;
            case 'content_generated':
              console.log('✨ Content generated:', message.data);
              onContentGenerated?.(message.data);
              break;
            case 'content_generation_failed':
              console.error('❌ Content generation failed:', message.data);
              onContentGenerationFailed?.(message.data);
              break;
            default:
              console.log('❓ Unknown WebSocket event:', message.event);
          }
        } catch (error) {
          console.error('WebSocket message parsing error:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket disconnected:', event.code, event.reason);
        isConnectedRef.current = false;
        wsRef.current = null;

        // Attempt to reconnect after 3 seconds (unless manually closed)
        if (event.code !== 1000) {
          console.log('🔄 Attempting to reconnect WebSocket in 3s...');
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        isConnectedRef.current = false;
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }, [onUploadCompleted, onUploadReviewed, onAuthSuccess, onContentGenerated, onContentGenerationFailed]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close(1000, 'Manual disconnect');
    }
    
    isConnectedRef.current = false;
    wsRef.current = null;
  }, []);

  // Connect immediately (session validation happens on server)
  useEffect(() => {
    try {
      connectWebSocket();
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
    }

    return () => {
      disconnect();
    };
  }, [connectWebSocket, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected: isConnectedRef.current,
    disconnect
  };
}